import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, Button, Input, Field, Spinner, EmptyState, ErrorBanner } from '../components/ui';

interface SignerState {
  id: string;
  email: string;
  name?: string;
  status: 'pending' | 'signed' | 'declined';
  document: {
    id: string;
    filename: string;
    pdfUrl: string;
    fields: { id: string; type: 'signature' | 'initials' | 'date' | 'text'; page: number; x: number; y: number }[];
  };
}

export function SignPage() {
  const { token } = useParams<{ token: string }>();
  const [email, setEmail] = useState('');
  const [authed, setAuthed] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const authMut = useMutation({
    mutationFn: () => api.post<{ authed: true }>('/sign/auth', { token, email }),
    onSuccess: () => setAuthed(true),
    onError: (e) => setError(e instanceof Error ? e.message : 'Auth failed'),
  });

  const sessionQ = useQuery({
    queryKey: ['sign-session', token],
    queryFn: () => api.get<SignerState>(`/sign/${token}`),
    enabled: authed,
  });

  const completeMut = useMutation({
    mutationFn: () => api.post('/sign/complete', { token, signatureDataUrl: signature }),
    onError: (e) => setError(e instanceof Error ? e.message : 'Sign failed'),
  });

  const declineMut = useMutation({
    mutationFn: () => api.post('/sign/decline', { token }),
    onError: (e) => setError(e instanceof Error ? e.message : 'Decline failed'),
  });

  function captureSignature() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSignature(canvas.toDataURL('image/png'));
  }

  function clearSignature() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature(null);
  }

  useEffect(() => {
    if (!authed) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let drawing = false;

    function pos(e: MouseEvent | TouchEvent) {
      const rect = canvas!.getBoundingClientRect();
      const p = 'touches' in e ? e.touches[0] : e;
      return { x: p.clientX - rect.left, y: p.clientY - rect.top };
    }

    function start(e: MouseEvent | TouchEvent) {
      e.preventDefault();
      drawing = true;
      const { x, y } = pos(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
    }

    function move(e: MouseEvent | TouchEvent) {
      if (!drawing) return;
      e.preventDefault();
      const { x, y } = pos(e);
      ctx.lineTo(x, y);
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.stroke();
    }

    function end() {
      drawing = false;
    }

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start);
    canvas.addEventListener('touchmove', move);
    canvas.addEventListener('touchend', end);
    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, [authed]);

  if (!authed) {
    return (
      <div style={{ maxWidth: 440, margin: '80px auto', padding: 'var(--space-4)' }}>
        <Card>
          <h1 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-2xl)', fontWeight: 700 }}>Sign document</h1>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Enter your email to access the document.</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!email) return setError('Email required');
              authMut.mutate();
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}
          >
            <Field label="Email">
              <Input value={email} onChange={setEmail} type="email" autoFocus />
            </Field>
            <ErrorBanner error={error} />
            <Button type="submit" disabled={authMut.isPending}>
              {authMut.isPending ? 'Verifying…' : 'Continue'}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  if (sessionQ.isLoading) return <Spinner />;
  if (!sessionQ.data) {
    return (
      <Card style={{ maxWidth: 480, margin: '80px auto' }}>
        <EmptyState title="Unable to load document" description="The link may be invalid or expired." />
      </Card>
    );
  }

  const session = sessionQ.data;
  const completed = completeMut.isSuccess;
  const declined = declineMut.isSuccess;

  if (completed) {
    return (
      <Card style={{ maxWidth: 480, margin: '80px auto', textAlign: 'center' }}>
        <div style={{ fontSize: 48 }}>✓</div>
        <h2 style={{ margin: 'var(--space-2) 0' }}>Thank you</h2>
        <p>Your signature has been recorded. A copy has been emailed to you.</p>
      </Card>
    );
  }

  if (declined) {
    return (
      <Card style={{ maxWidth: 480, margin: '80px auto', textAlign: 'center' }}>
        <h2 style={{ margin: 'var(--space-2) 0' }}>Declined</h2>
        <p>The sender has been notified.</p>
      </Card>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: '40px auto', padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <Card>
        <h1 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 700 }}>{session.document.filename}</h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
          Signing as {session.name ?? session.email}
        </p>
        <iframe
          src={session.document.pdfUrl}
          style={{ width: '100%', height: 500, border: '1px solid var(--color-border-primary)', borderRadius: 'var(--radius-md)', marginTop: 'var(--space-3)' }}
        />
      </Card>

      <Card>
        <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-base)', fontWeight: 600 }}>Your signature</h3>
        <canvas
          ref={canvasRef}
          width={600}
          height={150}
          style={{
            width: '100%',
            maxWidth: 600,
            height: 150,
            background: 'var(--color-surface-secondary)',
            borderRadius: 'var(--radius-md)',
            border: '2px dashed var(--color-border-primary)',
            touchAction: 'none',
          }}
        />
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={clearSignature}>
            Clear
          </Button>
          <Button variant="secondary" onClick={captureSignature}>
            Capture
          </Button>
        </div>
      </Card>

      <ErrorBanner error={error} />

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
        <Button variant="danger" onClick={() => declineMut.mutate()} disabled={declineMut.isPending}>
          Decline
        </Button>
        <Button onClick={() => completeMut.mutate()} disabled={!signature || completeMut.isPending}>
          {completeMut.isPending ? 'Submitting…' : 'Submit signature'}
        </Button>
      </div>
    </div>
  );
}
